import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Heart, Eye, X, Sparkles, ChevronDown, Filter } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BASE_URL, ENDPOINTS, getImageUrl, addToCart, addToWishlist } from '../apiConfig';
import QuickViewModal from '../components/home/QuickViewModal';
import './ShopPage.css';
import OutOfStockPopup from '../components/common/OutOfStockPopup';

gsap.registerPlugin(ScrollTrigger);

/* ── Animation SVGs ── */
const CartSVG = ({ id, className }) => (
  <svg id={id} className={className} xmlns="http://www.w3.org/2000/svg"
    width="28" height="28" viewBox="0 0 24 24" fill="none"
    stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const PerfumeSVG = ({ id }) => (
  <svg id={id} className="bs-perfume-svg" xmlns="http://www.w3.org/2000/svg"
    width="28" height="34" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2" style={{ display: 'none', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 5 }}>
    <rect x="9" y="2" width="6" height="4" rx="1" fill="rgba(0, 0, 0, 0.20)" />
    <rect x="5" y="6" width="14" height="16" rx="2" stroke="#0a0a0a" />
    <rect x="8" y="10" width="8" height="6" rx="0.5" stroke="#0a0a0a" strokeWidth="1" />
    <path d="M12 10v6" strokeWidth="1" strokeOpacity="0.5" />
  </svg>
);

const CheckSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("latest");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(window.innerWidth > 1024);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [added, setAdded] = useState({});
  const [animating, setAnimating] = useState({});
  const [oosPopup, setOosPopup] = useState({ open: false, variantName: '', productName: '', product: null });

  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wishlistItems') || '[]');
    } catch { return []; }
  });

  const gridRef = useRef(null);
  const mainRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();

    // Staggered Entrance Animation
    const ctx = gsap.context(() => {
      gsap.from(".hero-content > *", {
        y: 40,
        opacity: 0,
        duration: 1.2,
        stagger: 0.2,
        ease: "power4.out"
      });
    }, mainRef);

    return () => {
      ctx.revert();
    };
  }, []);

  useEffect(() => {
    // Silent polling for real-time stock updates
    const pollInterval = setInterval(() => {
      fetchData(false);
    }, 5000);
    return () => clearInterval(pollInterval);
  }, []);


  const fetchData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [prodRes, catRes] = await Promise.all([
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`, { headers }),
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_CATEGORIES}`, { headers })
      ]);

      const prodData = await prodRes.json();
      const catData = await catRes.json();

      const rawProds = Array.isArray(prodData.data) ? prodData.data : (Array.isArray(prodData) ? prodData : []);
      const parsedProds = rawProds.map(item => ({
        ...item,
        _id: item._id || item.id,
        id: item._id || item.id
      }));
      const parsedCats = Array.isArray(catData.data) ? catData.data : (Array.isArray(catData) ? catData : []);

      setProducts(parsedProds);
      setCategories(parsedCats);
    } catch (error) {
      console.error("Error fetching shop data:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const getFilteredProducts = () => {
    let filtered = products.filter(p => {
      const prodCatId = typeof p.categoryId === 'object' ? p.categoryId?._id : p.categoryId;
      const matchCategory = activeCategory === "ALL" || prodCatId === activeCategory;
      const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchType = selectedTypes.length === 0 || selectedTypes.some(type => 
        (p.type && p.type.toLowerCase() === type.toLowerCase()) || 
        (p.name && p.name.toLowerCase().includes(type.toLowerCase())) ||
        (p.tags && p.tags.some(tag => tag.toLowerCase().includes(type.toLowerCase())))
      );
      const matchGender = selectedGenders.length === 0 || selectedGenders.some(gender => {
        const g = gender.toLowerCase();
        const pGen = (p.gender || '').toLowerCase();
        const pName = (p.name || '').toLowerCase();
        
        if (pGen === g) return true;
        
        // Exact word match in name to prevent "women" from matching "men"
        const nameHasWord = new RegExp(`\\b${g}\\b`).test(pName);
        if (nameHasWord) return true;
        
        const tagsHaveWord = (p.tags || []).some(tag => new RegExp(`\\b${g}\\b`).test(tag.toLowerCase()));
        if (tagsHaveWord) return true;

        if (g === 'unisex' && (!pGen || pGen === '')) return true;
        
        return false;
      });
      
      const isOutOfStock = 
        p.status === 'OutOfStock' ||
        p.stockStatus === 'OutOfStock' ||
        (p.variants && p.variants.length > 0
          ? p.variants.every(v =>
              Number(v.stock ?? v.quantity ?? 1) <= 0 || v.inStock === false || String(v.inStock) === 'false'
            )
          : (
            String(p.stock) === '0' ||
            String(p.quantity) === '0' ||
            String(p.countInStock) === '0' ||
            p.inStock === false ||
            String(p.inStock) === 'false' ||
            p.isAvailable === false ||
            String(p.isAvailable) === 'false'
          )
        );

      const availabilityStatus = isOutOfStock ? 'OUT OF STOCK' : 'IN STOCK';
      const matchAvailability = selectedAvailability.length === 0 || selectedAvailability.includes(availabilityStatus);

      return matchCategory && matchSearch && matchType && matchGender && matchAvailability;
    });

    const getPrice = (prod) => {
      const v = prod.variants?.find(v => v.isDefault) || prod.variants?.[0] || {};
      return v.finalPrice || v.price || prod.price || 0;
    };

    if (sortBy === "price-low") {
      filtered.sort((a, b) => getPrice(a) - getPrice(b));
    } else if (sortBy === "price-high") {
      filtered.sort((a, b) => getPrice(b) - getPrice(a));
    } else if (sortBy === "latest") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === "name") {
      filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return filtered;
  };

  const toggleWishlist = (product) => {
    let updated;
    const prodId = product._id || product.id;
    if (wishlist.find(item => (item._id || item.id) === prodId)) {
      updated = wishlist.filter(item => (item._id || item.id) !== prodId);
    } else {
      updated = [...wishlist, product];
      addToWishlist(prodId).catch(console.error);
    }
    setWishlist(updated);
    localStorage.setItem('wishlistItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    e.preventDefault();
    const id = product._id || product.id;

    // The card adds the default (or first) variant — check that specific one
    const defaultVariant = product.variants?.find(v => v.isDefault) || product.variants?.[0] || null;
    const defaultVariantStock = defaultVariant
      ? Number(defaultVariant.stock ?? defaultVariant.quantity ?? 1)
      : (product.stock ?? product.quantity ?? 1);

    const isDefaultVariantOOS =
      product.status === 'OutOfStock' ||
      product.stockStatus === 'OutOfStock' ||
      defaultVariantStock <= 0 ||
      (defaultVariant && (defaultVariant.inStock === false || String(defaultVariant.inStock) === 'false')) ||
      (!defaultVariant && (product.inStock === false || product.isAvailable === false));

    if (isDefaultVariantOOS) {
      const outVariantName = defaultVariant
        ? (defaultVariant.size || defaultVariant.volume || defaultVariant.name || '')
        : '';
      // Check if any other variant has stock — offer Quick View if so
      const hasOtherStock = product.variants && product.variants.length > 1 &&
        product.variants.some(v => {
          if (v === defaultVariant) return false;
          return Number(v.stock ?? v.quantity ?? 1) > 0 && v.inStock !== false && String(v.inStock) !== 'false';
        });
      setOosPopup({ open: true, variantName: outVariantName, productName: product.name, product, hasOtherStock });
      return;
    }

    if (animating[id]) return;

    const btn = document.getElementById(`bag-btn-${id}`);
    const cart = document.getElementById(`bag-cart-${id}`);
    const productSvg = document.getElementById(`bag-product-${id}`);
    const label = document.getElementById(`bag-label-${id}`);
    const variantId = product.variants?.[0]?._id || product.variants?.[0]?.id || null;
    if (!btn || !cart || !productSvg || !label) {
      // Fallback if elements not found
      addToCart(id, variantId, 1).catch(console.error);
      return;
    }

    // Call API to add to cart
    addToCart(id, variantId, 1).catch(console.error);

    const ripple = document.createElement('span');
    ripple.className = 'bs-ripple';
    const r = btn.getBoundingClientRect();
    ripple.style.left = `${e.clientX - r.left}px`;
    ripple.style.top = `${e.clientY - r.top}px`;
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 700);

    setAnimating(prev => ({ ...prev, [id]: true }));

    const tl = gsap.timeline({
      onComplete: () => {
        setAnimating(prev => ({ ...prev, [id]: false }));
        setAdded(prev => ({ ...prev, [id]: true }));
        setTimeout(() => setAdded(prev => ({ ...prev, [id]: false })), 2000);
      }
    });

    tl.to(label, { opacity: 0, y: -15, duration: 0.3, ease: 'power2.in' });
    tl.fromTo(cart,
      { x: -160, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.6, ease: 'back.out(1.2)' }
    );
    tl.fromTo(productSvg,
      { y: -120, opacity: 0, scale: 0.6, display: 'block' },
      { y: -10, opacity: 1, scale: 1, duration: 0.6, ease: 'bounce.out' },
      '-=0.1'
    );
    tl.to(cart, { y: 3, duration: 0.1, yoyo: true, repeat: 1 });
    tl.to([cart, productSvg], { x: 160, opacity: 0, duration: 0.8, ease: 'power2.in' }, '+=0.5');
    tl.to(btn, { scale: 0.94, duration: 0.1, yoyo: true, repeat: 1 }, '-=0.4');
    tl.set([cart, productSvg, label], { clearProps: 'all' });
    tl.set(productSvg, { display: 'none' });
  };

  const filteredItems = getFilteredProducts();
  
  // Luxury Fallbacks so the sidebar filters are never empty or blank
  const extractedTypes = [...new Set(products.map(p => p.type).filter(Boolean))];
  // eslint-disable-next-line no-unused-vars
  const dynamicTypes = extractedTypes.length > 0 ? extractedTypes : ['Eau de Parfum', 'Extrait de Parfum', 'Eau de Toilette', 'Eau de Cologne'];
  
  const extractedGenders = [...new Set(products.map(p => p.gender).filter(Boolean))];
  // eslint-disable-next-line no-unused-vars
  const dynamicGenders = extractedGenders.length > 0 ? extractedGenders : ['Men', 'Women', 'Unisex'];

  // eslint-disable-next-line no-unused-vars
  const handleToggleFilter = (item, state, setState) => {
    // Single selection logic: If already selected, clear it. Otherwise, select only this item.
    if (state.includes(item)) {
      setState([]);
    } else {
      setState([item]);
    }
    
    // Auto-close sidebar on mobile/tablet after selection
    if (window.innerWidth <= 1024) {
      setShowFilters(false);
    }
  };

  return (
    <div className="shop-page-elite" ref={mainRef}>
      <div className="shop-fixed-bg"></div>
      {/* Hero Section - Functional & Clean */}
      <section className="shop-archive-hero">
        <div className="hero-content">
          <div className="breadcrumb-elite">HOME / SHOP / ARCHIVE</div>
          <div className="hero-main-title">
            <h1 className="hero-title">THE FRAGRANCE</h1>
            <div className="hero-title-divider">
              <span className="line"></span>
              <span className="accent-text">COLLECTION</span>
              <span className="line"></span>
            </div>
          </div>
          <p className="hero-desc">Explore our curated archives of exceptional essences, masterfully blended for the discerning nose.</p>
        </div>
        <div className="hero-floating-elements">
          <div className="float-circle c1"></div>
          <div className="float-circle c2"></div>
        </div>
      </section>

      <div className="shop-interface">
        {/* Dynamic Collection Banner */}
        {activeCategory !== "ALL" && (
          <div className="collection-focus-banner">
            <div className="banner-context">EXPLORING COLLECTION</div>
            <h2 className="banner-title">{categories.find(c => c._id === activeCategory)?.name}</h2>
            <div className="banner-divider"></div>
            <p className="banner-desc">
              {categories.find(c => c._id === activeCategory)?.description || "A dedicated selection of signature scents from this exclusive range."}
            </p>
          </div>
        )}
        {/* Quick Filter Bar */}
        <div className="shop-controls-bar">
          <div className="control-left">
            <button className={`toggle-filter-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <Filter size={18} />
              <span>{showFilters ? 'HIDE FILTERS' : 'SHOW FILTERS'}</span>
            </button>
            <div className="results-count">SHOWING {filteredItems.length} RESULTS</div>
          </div>

          <div className="control-right">
            <div className="search-mini-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="SEARCH SCENT..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="sort-select-box">
              <span>SORT:</span>
              <div 
                className={`custom-sort-dropdown ${isSortOpen ? 'open' : ''}`} 
                onMouseLeave={() => setIsSortOpen(false)}
              >
                <div className="sort-selected" onClick={() => setIsSortOpen(!isSortOpen)}>
                  {sortBy === 'latest' ? 'LATEST ARRIVALS' : sortBy === 'price-low' ? 'PRICE: LOW TO HIGH' : sortBy === 'price-high' ? 'PRICE: HIGH TO LOW' : 'ALPHABETICAL'}
                  <ChevronDown size={14} className="sort-chevron" />
                </div>
                <div className="sort-options">
                  <div className={`sort-option ${sortBy === 'latest' ? 'active' : ''}`} onClick={() => { setSortBy('latest'); setIsSortOpen(false); }}>LATEST ARRIVALS</div>
                  <div className={`sort-option ${sortBy === 'price-low' ? 'active' : ''}`} onClick={() => { setSortBy('price-low'); setIsSortOpen(false); }}>PRICE: LOW TO HIGH</div>
                  <div className={`sort-option ${sortBy === 'price-high' ? 'active' : ''}`} onClick={() => { setSortBy('price-high'); setIsSortOpen(false); }}>PRICE: HIGH TO LOW</div>
                  <div className={`sort-option ${sortBy === 'name' ? 'active' : ''}`} onClick={() => { setSortBy('name'); setIsSortOpen(false); }}>ALPHABETICAL</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="shop-layout">
          {/* Advanced Sidebar Filters */}
          <aside className={`shop-sidebar-elite ${showFilters ? 'visible' : 'hidden'}`}>
            <div className="sidebar-header-elite">
              <h4 className="block-title">FILTERS</h4>
              <button className="mobile-close-sidebar" onClick={() => setShowFilters(false)}>
                <X size={20} />
              </button>
            </div>
            
            {/* Sidebar Search Bar */}
            <div className="filter-block sidebar-search-block">
              <div className="sidebar-search-box">
                <Search size={16} className="sidebar-search-icon" />
                <input
                  type="text"
                  placeholder="SEARCH ARCHIVE..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="sidebar-search-clear" onClick={() => setSearchQuery('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="filter-block">
              <h4 className="block-title">CATEGORIES</h4>
              <div className="category-list-elite">
                <button
                  className={`cat-item-elite ${activeCategory === "ALL" ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory("ALL");
                    if (window.innerWidth <= 1024) setShowFilters(false);
                  }}
                >
                  ALL FRAGRANCES
                </button>
                {Array.isArray(categories) && categories.map(cat => (
                  <button
                    key={cat._id}
                    className={`cat-item-elite ${activeCategory === cat._id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(cat._id);
                      if (window.innerWidth <= 1024) setShowFilters(false);
                    }}
                  >
                    {cat.name.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>





            <div className="sidebar-promo">
              <div className="promo-card">
                <Sparkles size={24} color="#d4af37" />
                <h5>CONSULTATION</h5>
                <p>Can't decide? Let our experts find your signature scent.</p>
                <button>BOOK NOW</button>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <main className="shop-grid-area">
            {loading ? (
              <div className="elite-loader">
                <div className="spinner"></div>
                <p>SYNCHRONIZING ARCHIVES...</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="no-results-elite">
                <div className="no-results-glass-card">
                  <div className="no-results-icon">
                    <Search size={60} strokeWidth={1} />
                  </div>
                  <h2 className="no-results-title">NO SCENTS FOUND</h2>
                  <p className="no-results-desc">Your quest for the perfect essence is unique. Try adjusting your filters or search query to find your match.</p>
                  <button
                    className="reset-archive-btn"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('ALL');
                      setSelectedTypes([]);
                      setSelectedGenders([]);
                      setSelectedAvailability([]);
                    }}
                  >
                    RESET ARCHIVE
                  </button>
                </div>
              </div>
            ) : (
              <div className="product-grid-elite" ref={gridRef}>
                {filteredItems.map(product => {
                  const isWishlisted = wishlist.some(item => (item._id || item.id) === (product._id || product.id));
                  const productImage = getImageUrl(product.images?.[0]);
                  const hoverImage = product.images?.[1] ? getImageUrl(product.images[1]) : productImage;
                  // OUT OF STOCK only when ALL variants have 0 stock
                  const isOutOfStock = 
                    product.status === 'OutOfStock' ||
                    product.stockStatus === 'OutOfStock' ||
                    (product.variants && product.variants.length > 0
                      ? product.variants.every(v =>
                          Number(v.stock ?? v.quantity ?? 1) <= 0 || v.inStock === false || String(v.inStock) === 'false'
                        )
                      : (
                          product.stock === 0 ||
                          product.quantity === 0 ||
                          product.inStock === false ||
                          product.isAvailable === false
                        )
                    );
                  return (
                    <div
                      key={product._id || product.id}
                      className="elite-product-card"
                    >
                      {/* ── Image Zone ── */}
                      <div
                        className="card-img-zone"
                        onClick={() => window.location.href = `/product/${product._id || product.id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Shimmer sweep */}
                        <div className="card-shimmer" />

                        {/* Product image */}
                        <img src={productImage} alt={product.name} loading="lazy" className={`card-img ${product.images?.[1] ? 'primary-img' : ''}`} />
                        {product.images?.[1] && (
                          <img src={hoverImage} alt={`${product.name} hover`} loading="lazy" className="card-img hover-img" />
                        )}

                        {/* Top row: badge + actions */}
                        <div className="card-top-row">
                          <span className="card-badge">PREMIUM</span>
                          <div className="card-actions">
                            <button
                              className={`card-action-btn ${isWishlisted ? 'wishlisted' : ''}`}
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleWishlist(product); }}
                            >
                              <Heart size={16} fill={isWishlisted ? '#ef4444' : 'none'} color={isWishlisted ? '#ef4444' : '#fff'} />
                            </button>
                            <button
                              className="card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setSelectedProduct(product);
                              }}
                            >
                              <Eye size={16} color="#fff" />
                            </button>
                          </div>
                        </div>

                        {/* Glass info panel slides up from bottom */}
                        <div className="card-glass-panel">
                          <div className="card-panel-meta">
                            <div className="brand-group">
                              <span className="card-panel-brand">{product.brand || 'Escentrum Private'}</span>
                              <div className="brand-dot"></div>
                            </div>
                            {product.type && <span className="card-panel-type">{product.type}</span>}
                          </div>
                          <h3 className="card-panel-name">{product.name}</h3>
                          <div className="card-panel-footer">
                            <span className="card-panel-price">
                              Rs. {(() => {
                                const v = product.variants?.find(v => v.isDefault) || product.variants?.[0] || {};
                                const pr = v.finalPrice || v.price || product.price || 0;
                                return pr.toLocaleString();
                              })()}
                            </span>
                            <div className="card-btn-wrap">
                              <PerfumeSVG id={`bag-product-${product._id || product.id}`} />
                              <button
                                id={`bag-btn-${product._id || product.id}`}
                                className={`card-panel-bag ${isOutOfStock ? 'is-out' : ''} ${animating[product._id || product.id] ? 'is-animating' : ''} ${added[product._id || product.id] ? 'is-added' : ''}`}
                                onClick={(e) => handleAddToCart(e, product)}
                              >
                                <CartSVG id={`bag-cart-${product._id || product.id}`} className="bs-cart-icon" />
                                <span id={`bag-label-${product._id || product.id}`} className="bs-btn-label">
                                  {isOutOfStock ? (
                                    "OUT OF STOCK"
                                  ) : added[product._id || product.id] ? (
                                    <span className="bs-success-text">
                                      <CheckSVG /> ADDED
                                    </span>
                                  ) : (
                                    "ADD TO BAG"
                                  )}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Bottom gradient for readability */}
                        <div className="card-bottom-fade" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Quick View Modal */}
      {selectedProduct && (
        <QuickViewModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* Out-of-Stock Popup */}
      <OutOfStockPopup
        isOpen={oosPopup.open}
        onClose={() => setOosPopup({ open: false, variantName: '', productName: '', product: null, hasOtherStock: false })}
        variantName={oosPopup.variantName}
        productName={oosPopup.productName}
        hasQuickView={!!oosPopup.hasOtherStock}
        onOpenQuickView={() => {
          setSelectedProduct(oosPopup.product);
          setOosPopup({ open: false, variantName: '', productName: '', product: null, hasOtherStock: false });
        }}
      />
    </div>
  );
};

export default ShopPage;
